import { useCallback, useRef, memo, useEffect, useState } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import Selecto from 'react-selecto'

const DragComponent = ({
  selectoRef,
  scrollEl,
  selectoDragMarker,
  gridOrder,
  colCount,
  getIdMap,
  selectedAssetMap,
  setSelectedAssetMap,
  assetCountMap,
  setFocusedIndex,
  keyActiveElementRef,
}) => {
  const scrollBreakpointMap = useRef<[string, number][] | null>(null)
  const currentYInContainer = useRef<number | null>(null)
  const gridElementsHeightMap = useRef<Record<string, number>>({})

  const computeScrollBreakpoints = useCallback(() => {
    const orderedEntries: [string, number][] = [['topPadding', 24]]
    let cumulativeHeight = 24
    for (let i = 0; i < gridOrder.length; i++) {
      const gridType = gridOrder[i]

      const headingHeight = (
        document.getElementsByClassName(`${gridType}-heading`)[0] as HTMLElement
      )?.offsetHeight
      if (headingHeight) {
        cumulativeHeight += headingHeight
        orderedEntries.push([`${gridType}-heading`, cumulativeHeight])
      }

      const gridEl = document.getElementsByClassName(`${gridType}-grid`)[0] as
        | HTMLElement
        | undefined
      const gridElHeight = gridEl?.offsetHeight
      if (gridElHeight) {
        cumulativeHeight += gridElHeight
        orderedEntries.push([`${gridType}-grid`, cumulativeHeight])

        const firstChild = gridEl.firstElementChild as HTMLElement | null
        if (firstChild) {
          gridElementsHeightMap.current[gridType] =
            firstChild.getBoundingClientRect().height
        }
      }

      const seeMoreHeight = (
        document.getElementsByClassName(
          `${gridType}-see-more`,
        )[0] as HTMLElement
      )?.offsetHeight
      if (seeMoreHeight) {
        cumulativeHeight += seeMoreHeight
        orderedEntries.push([`${gridType}-see-more`, cumulativeHeight])
      }
    }

    const bottomPadding = (
      document.getElementsByClassName('bottom-padding')[0] as HTMLElement
    )?.offsetHeight
    if (bottomPadding) {
      cumulativeHeight += bottomPadding
      orderedEntries.push(['bottom-padding', cumulativeHeight])
    }
    scrollBreakpointMap.current = orderedEntries
  }, [gridOrder])

  useEffect(() => {
    scrollBreakpointMap.current = null
    gridElementsHeightMap.current = {}
  }, [colCount, gridOrder])

  const setSelectedAssetOnDrag = useCallback(
    (
      useToggle: boolean,
      multiGridSelection: Record<string, Set<string>>,
      lastId: string,
      lastGrid: string,
      lastIdx: number,
    ) => {
      unstable_batchedUpdates(() => {
        let needToSetFocus = true
        for (let i = 0; i < gridOrder.length; i++) {
          const grid = gridOrder[i]

          if (!multiGridSelection[grid]) {
            if (!useToggle) {
              selectedAssetMap[grid].clear()
              setSelectedAssetMap[grid](selectedAssetMap[grid])
            } else {
              // no action needed as nothing new selected in this grid
            }
          } else {
            if (!useToggle) {
              selectedAssetMap[grid] = multiGridSelection[grid]
              setSelectedAssetMap[grid](selectedAssetMap[grid])
            } else {
              if (multiGridSelection[grid].size) {
                const next = selectedAssetMap[grid]
                for (const i of multiGridSelection[grid]) {
                  if (next.has(i)) {
                    if (i === lastId && grid === lastGrid) {
                      needToSetFocus = false
                    }
                    next.delete(i)
                  } else {
                    next.add(i)
                  }
                }
                setSelectedAssetMap[grid](next)
              }
            }
          }
        }

        const newId = getIdMap[lastGrid](lastIdx)
        if (
          needToSetFocus &&
          typeof lastIdx === 'number' &&
          lastGrid &&
          newId
        ) {
          setFocusedIndex({ index: lastIdx, type: lastGrid, id: newId })
        } else {
          setFocusedIndex(null)
        }
      })
      return true
    },
    [setSelectedAssetMap, selectedAssetMap, gridOrder],
  )

  const setSelectedAssetSingleGrid = useCallback(
    (
      useToggle: boolean,
      gridType: string,
      selectedIds: Set<string>,
      lastId: string,
      lastGrid: string,
      lastIdx: number,
    ) => {
      unstable_batchedUpdates(() => {
        let needToSetFocus = true
        if (!useToggle) {
          selectedAssetMap[gridType] = selectedIds
          setSelectedAssetMap[gridType](selectedIds)
          for (let i = 0; i < gridOrder.length; i++) {
            const grid = gridOrder[i]
            if (grid !== gridType) {
              selectedAssetMap[grid].clear()
              setSelectedAssetMap[grid](selectedAssetMap[grid])
            }
          }
        } else {
          if (selectedIds.size) {
            const next = selectedAssetMap[gridType]
            for (const i of selectedIds) {
              if (next.has(i)) {
                if (i === lastId && gridType === lastGrid) {
                  needToSetFocus = false
                }
                next.delete(i)
              } else {
                next.add(i)
              }
            }
            setSelectedAssetMap[gridType](next)
          }
        }

        const newId = getIdMap[lastGrid](lastIdx)
        if (
          needToSetFocus &&
          typeof lastIdx === 'number' &&
          lastGrid &&
          newId
        ) {
          setFocusedIndex({ index: lastIdx, type: lastGrid, id: newId })
        } else {
          setFocusedIndex(null)
        }
      })
      return true
    },
    [setSelectedAssetMap, selectedAssetMap, gridOrder],
  )

  const rightMostElement = (selectedTargets: HTMLDivElement[] | null) => {
    if (!selectedTargets || !selectedTargets.length) return null
    let lastElement: HTMLDivElement = selectedTargets[0]
    for (let i = 0; i < selectedTargets.length; i++) {
      const el = selectedTargets[i]
      const leftOffset = el.offsetLeft
      const topOffset = el.offsetTop
      if (leftOffset >= lastElement.offsetLeft) {
        lastElement = el
      }
    }
    return lastElement
  }

  const leftMostElement = (selectedTargets: HTMLDivElement[]) => {
    if (!selectedTargets.length) return null
    let lastElement: HTMLDivElement = selectedTargets[0]
    for (let i = 0; i < selectedTargets.length; i++) {
      const el = selectedTargets[i]
      const leftOffset = el.offsetLeft
      const topOffset = el.offsetTop
      if (leftOffset <= lastElement.offsetLeft) {
        lastElement = el
      }
    }
    return lastElement
  }

  const topRightMostElement = (selectedTargets: HTMLDivElement[]) => {
    if (!selectedTargets.length) return null
    let lastElement: HTMLDivElement = selectedTargets[0]
    const lastGrid = lastElement.getAttribute('data-grid-type') // to check only on the last grid , because in cases
    // where user is dragging from one grid to other, then below grid will have
    // top offset starting from zero , resulting in lesser value than top grid.

    for (let i = 0; i < Math.min(colCount, selectedTargets.length); i++) {
      // checking for colCount number of elements only is sufficient
      // as guarantee to cover full top row
      const el = selectedTargets[i]
      const leftOffset = el.offsetLeft
      const topOffset = el.offsetTop
      const elGrid = el.getAttribute('data-grid-type')
      if (
        topOffset <= lastElement.offsetTop &&
        leftOffset >= lastElement.offsetLeft &&
        elGrid === lastGrid
      ) {
        lastElement = el
      }
    }
    return lastElement
  }

  return (
    <Selecto
      dragContainer={'.media-library-layout'}
      selectableTargets={['.drag-selectable-element']}
      hitRate={'0px'}
      ref={selectoRef}
      selectByClick={false}
      selectFromInside={true}
      keyContainer={window}
      continueSelect={false}
      clickBySelectEnd={false}
      preventClickEventOnDrag={true}
      preventRightClick={true}
      onScroll={({ direction }) => {
        scrollEl.scrollBy(direction[0] * 5, direction[1] * 5)
      }}
      dragCondition={e => {
        const target = e.inputEvent.target as HTMLElement
        return !target.classList.contains('media-library-layout')
      }}
      onDragStart={e => {
        const scrollOffset = scrollEl.scrollTop
        const clientY = e.inputEvent.clientY
        const parentOffsetTop = scrollEl.offsetTop

        currentYInContainer.current = scrollOffset + (clientY - parentOffsetTop)
      }}
      continueSelectWithoutDeselect={true}
      scrollOptions={{
        container: scrollEl,
        getScrollPosition: () => {
          return [scrollEl.scrollLeft, scrollEl.scrollTop]
        },
        checkScrollEvent: true,
        threshold: 30,
        throttleTime: 16,
      }}
      onDragEnd={e => {
        if (e.isClick) {
          return
        }

        if (scrollBreakpointMap.current === null) {
          computeScrollBreakpoints()
        }

        if (scrollBreakpointMap.current === null) {
          return
        }

        selectoDragMarker.current = { x: e.distX, y: e.distY }
        if (!e.isDrag) {
          return
        }

        const { metaKey, ctrlKey, shiftKey } = e.inputEvent as
          | MouseEvent
          | KeyboardEvent
        const useToggle = metaKey || ctrlKey || shiftKey
        const disX = e.distX
        const disY = e.distY
        let lastIdx: number | null = null
        let lastGrid: string | null | undefined = null
        let lastId: string | null = null
        let firstGrid
        let startRowPosition
        const currentY = currentYInContainer.current
        if (currentY === null) return
        let classInWhichCurrentYFallsInto
        for (const [key, value] of scrollBreakpointMap.current) {
          if (currentY <= value) {
            classInWhichCurrentYFallsInto = key
            break
          }
        }

        const classArray = scrollBreakpointMap.current.map(([key]) => key)

        if (
          currentY >
          scrollBreakpointMap.current[scrollBreakpointMap.current.length - 1][1]
        ) {
          classInWhichCurrentYFallsInto = 'bottom-padding'
        }
        if (!classInWhichCurrentYFallsInto) {
          return
        }

        if (disY >= 0) {
          if (classInWhichCurrentYFallsInto === 'topPadding') {
            firstGrid = gridOrder[0]
            startRowPosition = 1
          } else if (classInWhichCurrentYFallsInto.endsWith('heading')) {
            firstGrid = classInWhichCurrentYFallsInto.split('-')[0]
            startRowPosition = 1
          } else if (classInWhichCurrentYFallsInto.endsWith('grid')) {
            firstGrid = classInWhichCurrentYFallsInto.split('-')[0]
            const classIndex = classArray.indexOf(classInWhichCurrentYFallsInto)
            const prevClass = classArray[classIndex - 1]
            const offsetYInGridContainer =
              currentY -
              scrollBreakpointMap.current.filter(
                ([key]) => key === prevClass,
              )[0][1]
            const elementHeight = gridElementsHeightMap.current[firstGrid]
            if (!elementHeight) return
            const gridGap = 16
            const heightRemainder =
              offsetYInGridContainer % (elementHeight + gridGap)
            startRowPosition = Math.ceil(
              offsetYInGridContainer / (elementHeight + gridGap),
            )
            if (heightRemainder > elementHeight) {
              startRowPosition += 1
            }
          } else if (classInWhichCurrentYFallsInto.endsWith('see-more')) {
            const grid = classInWhichCurrentYFallsInto.split('-')[0]
            const gridIndex = gridOrder.indexOf(grid)
            if (gridIndex === gridOrder.length - 1) {
              // do nothing as it's the last see more
              return
            }
            firstGrid = gridOrder[gridIndex + 1]
            startRowPosition = 1
          } else if (classInWhichCurrentYFallsInto === 'bottom-padding') {
            // do nothing
            return
          }
        } else {
          if (classInWhichCurrentYFallsInto === 'topPadding') {
            return
          } else if (classInWhichCurrentYFallsInto.endsWith('heading')) {
            const grid = classInWhichCurrentYFallsInto.split('-')[0]
            if (firstGrid === gridOrder[0]) {
              return
            }
            const gridIndex = gridOrder.indexOf(grid)
            firstGrid = gridOrder[gridIndex - 1]
            startRowPosition = Math.ceil(assetCountMap[firstGrid] / colCount)
          } else if (classInWhichCurrentYFallsInto.endsWith('grid')) {
            firstGrid = classInWhichCurrentYFallsInto.split('-')[0]
            const classIndex = classArray.indexOf(classInWhichCurrentYFallsInto)
            const prevClass = classArray[classIndex - 1]
            const offsetYInGridContainer =
              currentY -
              scrollBreakpointMap.current.filter(
                ([key]) => key === prevClass,
              )[0][1]
            const elementHeight = gridElementsHeightMap.current[firstGrid]
            if (!elementHeight) return
            const gridGap = 16
            startRowPosition = Math.ceil(
              offsetYInGridContainer / (elementHeight + gridGap),
            )
          } else if (classInWhichCurrentYFallsInto.endsWith('see-more')) {
            firstGrid = classInWhichCurrentYFallsInto.split('-')[0]
            startRowPosition = Math.ceil(assetCountMap[firstGrid] / colCount)
          } else if (classInWhichCurrentYFallsInto === 'bottom-padding') {
            firstGrid = gridOrder[gridOrder.length - 1]
            startRowPosition = Math.ceil(assetCountMap[firstGrid] / colCount)
          }
        }

        // @ts-ignore
        const leftMostColumnElement = leftMostElement(e.currentTarget.selectedTargets)
        const leftIdx = Number(
          leftMostColumnElement?.getAttribute('data-index'),
        )
        if (Number.isNaN(leftIdx)) return
        const leftColumnPosition =
          (leftIdx + 1) % colCount === 0 ? colCount : (leftIdx + 1) % colCount

        // @ts-ignore
        const rightMostColumnElement = rightMostElement(e.currentTarget.selectedTargets)
        const rightIdx = Number(
          rightMostColumnElement?.getAttribute('data-index'),
        )
        if (Number.isNaN(rightIdx)) return
        const rightColumnPosition =
          (rightIdx + 1) % colCount === 0 ? colCount : (rightIdx + 1) % colCount

        const multiGridSelection = {}

        if (disX >= 0 && disY >= 0) {
          // dragged to bottom-right

          // @ts-ignore
          const bottomRightMostElement =e.currentTarget.selectedTargets[e.currentTarget.selectedTargets.length - 1]
          const idxRowStr = bottomRightMostElement?.getAttribute('data-index')

          lastIdx = Number(idxRowStr)
          lastGrid = bottomRightMostElement?.getAttribute('data-grid-type')

          if (
            Number.isNaN(lastIdx) ||
            !lastGrid ||
            !gridOrder.includes(lastGrid)
          )
            return

          lastId = getIdMap[lastGrid](lastIdx)
          if (!lastId) return

          const lastRowPosition = Math.ceil((lastIdx + 1) / colCount)
          const firstRowPosition = startRowPosition
          const firstColumnPosition = leftColumnPosition
          const lastColumnPosition = rightColumnPosition

          const firstGridIndex = gridOrder.indexOf(firstGrid)
          const lastGridIndex = gridOrder.indexOf(lastGrid)
          if (lastGrid === firstGrid) {
            const selectedIndexes = new Set<string>()
            for (let i = firstRowPosition; i <= lastRowPosition; i++) {
              for (let j = firstColumnPosition; j <= lastColumnPosition; j++) {
                const newIdx = (i - 1) * colCount + (j - 1)

                if (newIdx >= assetCountMap[lastGrid]) {
                  break
                }
                selectedIndexes.add(getIdMap[lastGrid](newIdx))
              }
            }
            setSelectedAssetSingleGrid(
              useToggle,
              lastGrid,
              selectedIndexes,
              lastId,
              lastGrid,
              lastIdx,
            )
          } else {
            if (firstGridIndex > lastGridIndex) return
            for (let g = firstGridIndex; g <= lastGridIndex; g++) {
              const gridType = gridOrder[g]
              const selectedIndexes = new Set<string>()
              if (g === firstGridIndex) {
                for (
                  let i = firstRowPosition;
                  i <= Math.ceil(assetCountMap[gridType] / colCount);
                  i++
                ) {
                  for (
                    let j = firstColumnPosition;
                    j <= lastColumnPosition;
                    j++
                  ) {
                    const newIdx = (i - 1) * colCount + (j - 1)
                    if (newIdx >= assetCountMap[gridType]) {
                      break
                    }
                    selectedIndexes.add(getIdMap[gridType](newIdx))
                  }
                }
              } else if (g === lastGridIndex) {
                for (let i = 1; i <= lastRowPosition; i++) {
                  for (
                    let j = firstColumnPosition;
                    j <= lastColumnPosition;
                    j++
                  ) {
                    const newIdx = (i - 1) * colCount + (j - 1)
                    if (newIdx >= assetCountMap[gridType]) {
                      break
                    }
                    selectedIndexes.add(getIdMap[gridType](newIdx))
                  }
                }
              } else {
                for (
                  let i = 1;
                  i <= Math.ceil(assetCountMap[gridType] / colCount);
                  i++
                ) {
                  for (
                    let j = firstColumnPosition;
                    j <= lastColumnPosition;
                    j++
                  ) {
                    const newIdx = (i - 1) * colCount + (j - 1)
                    if (newIdx >= assetCountMap[gridType]) {
                      break
                    }
                    selectedIndexes.add(getIdMap[gridType](newIdx))
                  }
                }
              }
              multiGridSelection[gridType] = selectedIndexes
            }

            setSelectedAssetOnDrag(
              useToggle,
              multiGridSelection,
              lastId,
              lastGrid,
              lastIdx,
            )
          }
        } else if (disX <= 0 && disY <= 0) {
          // dragged to top-left
          // @ts-ignore
          const topLeftMostElement = e.currentTarget.selectedTargets[0]
          const idxStr = topLeftMostElement?.getAttribute('data-index')
          lastGrid = topLeftMostElement?.getAttribute('data-grid-type')
          if (!idxStr || !lastGrid || !gridOrder.includes(lastGrid)) return

          lastIdx = Number(idxStr)
          if (Number.isNaN(lastIdx)) return

          lastId = getIdMap[lastGrid](lastIdx)
          if (!lastId) return

          const lastColumnPosition = leftColumnPosition
          const lastRowPosition = Math.ceil((lastIdx + 1) / colCount)
          const firstColumnPosition = rightColumnPosition
          const firstRowPosition = startRowPosition

          if (lastGrid === firstGrid) {
            const selectedIndexes = new Set<string>()
            for (let i = lastRowPosition; i <= firstRowPosition; i++) {
              for (let j = lastColumnPosition; j <= firstColumnPosition; j++) {
                const newIdx = (i - 1) * colCount + (j - 1)
                if (newIdx >= assetCountMap[firstGrid]) {
                  break
                }
                selectedIndexes.add(getIdMap[lastGrid](newIdx))
              }
            }
            setSelectedAssetSingleGrid(
              useToggle,
              lastGrid,
              selectedIndexes,
              lastId,
              lastGrid,
              lastIdx,
            )
          } else {
            const firstGridIndex = gridOrder.indexOf(firstGrid)
            const lastGridIndex = gridOrder.indexOf(lastGrid)
            if (firstGridIndex < lastGridIndex) return
            for (let g = lastGridIndex; g <= firstGridIndex; g++) {
              const gridType = gridOrder[g]
              const selectedIndexes = new Set<string>()
              if (g === firstGridIndex) {
                for (let i = 1; i <= firstRowPosition; i++) {
                  for (
                    let j = lastColumnPosition;
                    j <= firstColumnPosition;
                    j++
                  ) {
                    const newIdx = (i - 1) * colCount + (j - 1)
                    if (newIdx >= assetCountMap[gridType]) {
                      break
                    }
                    selectedIndexes.add(getIdMap[gridType](newIdx))
                  }
                }
              } else if (g === lastGridIndex) {
                for (
                  let i = lastRowPosition;
                  i <= Math.ceil(assetCountMap[gridType] / colCount);
                  i++
                ) {
                  for (
                    let j = lastColumnPosition;
                    j <= firstColumnPosition;
                    j++
                  ) {
                    const newIdx = (i - 1) * colCount + (j - 1)
                    if (newIdx >= assetCountMap[gridType]) {
                      break
                    }
                    selectedIndexes.add(getIdMap[gridType](newIdx))
                  }
                }
              } else {
                for (
                  let i = 1;
                  i <= Math.ceil(assetCountMap[gridType] / colCount);
                  i++
                ) {
                  for (
                    let j = lastColumnPosition;
                    j <= firstColumnPosition;
                    j++
                  ) {
                    const newIdx = (i - 1) * colCount + (j - 1)
                    if (newIdx >= assetCountMap[gridType]) {
                      break
                    }
                    selectedIndexes.add(getIdMap[gridType](newIdx))
                  }
                }
              }
              multiGridSelection[gridType] = selectedIndexes
            }
            setSelectedAssetOnDrag(
              useToggle,
              multiGridSelection,
              lastId,
              lastGrid,
              lastIdx,
            )
          }
        } else if (disX <= 0 && disY >= 0) {
          // dragged to bottom-left
          // @ts-ignore
          const lastElement = leftMostElement(e.currentTarget.selectedTargets) // this act as bottom-left most

          if (!lastElement) return
          const idxStr = lastElement?.getAttribute('data-index')
          lastGrid = lastElement?.getAttribute('data-grid-type')
          if (!idxStr || !lastGrid || !gridOrder.includes(lastGrid)) return

          lastIdx = Number(idxStr)
          if (Number.isNaN(lastIdx)) return

          lastId = getIdMap[lastGrid](lastIdx)
          if (!lastId) return

          const lastRowPosition = Math.ceil((lastIdx + 1) / colCount)
          const lastColumnPosition = leftColumnPosition

          const firstColumnPosition = rightColumnPosition
          const firstRowPosition = startRowPosition

          if (lastGrid === firstGrid) {
            const selectedIndexes = new Set<string>()
            for (let i = firstRowPosition; i <= lastRowPosition; i++) {
              for (let j = lastColumnPosition; j <= firstColumnPosition; j++) {
                const newIdx = (i - 1) * colCount + (j - 1)
                if (newIdx >= assetCountMap[lastGrid]) {
                  break
                }
                selectedIndexes.add(getIdMap[firstGrid](newIdx))
              }
            }
            setSelectedAssetSingleGrid(
              useToggle,
              lastGrid,
              selectedIndexes,
              lastId,
              lastGrid,
              lastIdx,
            )
          } else {
            const firstGridIndex = gridOrder.indexOf(firstGrid)
            const lastGridIndex = gridOrder.indexOf(lastGrid)
            if (firstGridIndex > lastGridIndex) return
            for (let g = firstGridIndex; g <= lastGridIndex; g++) {
              const gridType = gridOrder[g]
              const selectedIndexes = new Set<string>()
              if (g === firstGridIndex) {
                for (
                  let i = firstRowPosition;
                  i <= Math.ceil(assetCountMap[gridType] / colCount);
                  i++
                ) {
                  for (
                    let j = lastColumnPosition;
                    j <= firstColumnPosition;
                    j++
                  ) {
                    const newIdx = (i - 1) * colCount + (j - 1)
                    if (newIdx >= assetCountMap[gridType]) {
                      break
                    }
                    selectedIndexes.add(getIdMap[gridType](newIdx))
                  }
                }
              } else if (g === lastGridIndex) {
                for (let i = 1; i <= lastRowPosition; i++) {
                  for (
                    let j = lastColumnPosition;
                    j <= firstColumnPosition;
                    j++
                  ) {
                    const newIdx = (i - 1) * colCount + (j - 1)
                    if (newIdx >= assetCountMap[gridType]) {
                      break
                    }
                    selectedIndexes.add(getIdMap[gridType](newIdx))
                  }
                }
              } else {
                for (
                  let i = 1;
                  i <= Math.ceil(assetCountMap[gridType] / colCount);
                  i++
                ) {
                  for (
                    let j = lastColumnPosition;
                    j <= firstColumnPosition;
                    j++
                  ) {
                    const newIdx = (i - 1) * colCount + (j - 1)
                    if (newIdx >= assetCountMap[gridType]) {
                      break
                    }
                    selectedIndexes.add(getIdMap[gridType](newIdx))
                  }
                }
              }
              multiGridSelection[gridType] = selectedIndexes
            }
            setSelectedAssetOnDrag(
              useToggle,
              multiGridSelection,
              lastId,
              lastGrid,
              lastIdx,
            )
          }
        } else if (disX >= 0 && disY <= 0) {
          // dragged to top-right
          // @ts-ignore

          let lastElement = topRightMostElement(e.currentTarget.selectedTargets)
          if (!lastElement) return
          lastGrid = lastElement.getAttribute('data-grid-type')

          const idxStr = lastElement?.getAttribute('data-index')
          if (!idxStr || !lastGrid || !gridOrder.includes(lastGrid)) return

          lastIdx = Number(idxStr)
          if (Number.isNaN(lastIdx)) return

          lastId = getIdMap[lastGrid](lastIdx)
          if (!lastId) return
          const lastRowPosition = Math.ceil((lastIdx + 1) / colCount)
          const lastColumnPosition = rightColumnPosition
          const firstColumnPosition = leftColumnPosition
          const firstRowPosition = startRowPosition

          if (lastGrid === firstGrid) {
            const selectedIndexes = new Set<string>()
            for (let i = lastRowPosition; i <= firstRowPosition; i++) {
              for (let j = firstColumnPosition; j <= lastColumnPosition; j++) {
                const newIdx = (i - 1) * colCount + (j - 1)
                if (newIdx >= assetCountMap[lastGrid]) {
                  break
                }
                selectedIndexes.add(getIdMap[lastGrid](newIdx))
              }
            }
            setSelectedAssetSingleGrid(
              useToggle,
              lastGrid,
              selectedIndexes,
              lastId,
              lastGrid,
              lastIdx,
            )
          } else {
            const firstGridIndex = gridOrder.indexOf(firstGrid)
            const lastGridIndex = gridOrder.indexOf(lastGrid)
            if (lastGridIndex > firstGridIndex) return
            for (let g = lastGridIndex; g <= firstGridIndex; g++) {
              const gridType = gridOrder[g]
              const selectedIndexes = new Set<string>()
              if (g === firstGridIndex) {
                for (let i = 1; i <= firstRowPosition; i++) {
                  for (
                    let j = firstColumnPosition;
                    j <= lastColumnPosition;
                    j++
                  ) {
                    const newIdx = (i - 1) * colCount + (j - 1)
                    if (newIdx >= assetCountMap[gridType]) {
                      break
                    }
                    selectedIndexes.add(getIdMap[gridType](newIdx))
                  }
                }
              } else if (g === lastGridIndex) {
                for (
                  let i = lastRowPosition;
                  i <= Math.ceil(assetCountMap[gridType] / colCount);
                  i++
                ) {
                  for (
                    let j = firstColumnPosition;
                    j <= lastColumnPosition;
                    j++
                  ) {
                    const newIdx = (i - 1) * colCount + (j - 1)
                    if (newIdx >= assetCountMap[gridType]) {
                      break
                    }
                    selectedIndexes.add(getIdMap[gridType](newIdx))
                  }
                }
              } else {
                for (
                  let i = 1;
                  i <= Math.ceil(assetCountMap[gridType] / colCount);
                  i++
                ) {
                  for (
                    let j = firstColumnPosition;
                    j <= lastColumnPosition;
                    j++
                  ) {
                    const newIdx = (i - 1) * colCount + (j - 1)
                    if (newIdx >= assetCountMap[gridType]) {
                      break
                    }
                    selectedIndexes.add(getIdMap[gridType](newIdx))
                  }
                }
              }
              multiGridSelection[gridType] = selectedIndexes
            }
            setSelectedAssetOnDrag(
              useToggle,
              multiGridSelection,
              lastId,
              lastGrid,
              lastIdx,
            )
          }
        }
        keyActiveElementRef.current?.focus()
      }}
    ></Selecto>
  )
}

export default memo(DragComponent)
